import * as React from 'react';
import { InjectedRouter } from 'react-router';

import AdminDistrictSisDashboardCleverHeader from 'admin/rostering/components/AdminDistrictSisDashboardCleverHeader';
import AdminDistrictSisDashboardSftpHeader from 'admin/rostering/components/AdminDistrictSisDashboardSftpHeader';
import AdminSisSyncSchoolTable from 'admin/rostering/components/AdminSisSyncSchoolTable';
import Checkbox from 'components/Checkbox';
import Container from 'components/Container';
import CurrentUser from 'auth/CurrentUser';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import Text from 'components/Text';
import Time from 'components/Time';

import { AdminDistrictSisDashboard_organizations as Organization } from 'admin/rostering/queries/__generated__/AdminDistrictSisDashboard';

import './styles/AdminDistrictSisDashboard.scss';

type Props = {
  organization: Organization;
  orgId: number;
  syncMethod: string;
  router: InjectedRouter;
  handleRosteringEnforcementChange: (allowedToLeave: boolean) => void;
};

export default class AdminDistrictSisDashboard extends React.Component<Props> {
  renderHeader(organization: Organization) {
    const { syncMethod } = this.props;
    const {
      canOnlySeeReviewScreen,
      canOnlySeeRosteringStagingArea,
    } = organization.rosteringPermissions;

    const headerTitle = 'SIS sync';

    if (syncMethod === 'Clever') {
      return (
        <AdminDistrictSisDashboardCleverHeader
          title={headerTitle}
          subtitle={this.renderLastUploadInfo(organization)}
          orgUuid={organization.uuid}
          childOrganizations={organization.childOrganizations}
          canOnlySeeReviewScreen={canOnlySeeReviewScreen}
          canOnlySeeRosteringStagingArea={canOnlySeeRosteringStagingArea}
        />
      );
    } else if (syncMethod === 'SFTP') {
      return (
        <AdminDistrictSisDashboardSftpHeader
          title={headerTitle}
          subtitle={this.renderLastUploadInfo(organization)}
          orgUuid={organization.uuid}
          sisSyncOverview={organization.sisSyncOverview}
          childOrganizations={organization.childOrganizations}
          canOnlySeeReviewScreen={canOnlySeeReviewScreen}
          canOnlySeeRosteringStagingArea={canOnlySeeRosteringStagingArea}
        />
      );
    } else {
      return null;
    }
  }

  renderLastUploadInfo(organization: Organization) {
    if (
      !organization ||
      !organization.sisSyncOverview ||
      !organization.sisSyncOverview.districtStatus
    ) {
      return null;
    }

    const {
      lastUploadTimestamp,
      lastUploadFiles,
      filesZipUrl,
    } = organization.sisSyncOverview.districtStatus;

    if (!lastUploadTimestamp || !lastUploadFiles || lastUploadFiles.length === 0) {
      return 'Select a school to view its details.';
    }

    const date = <Time date={new Date(lastUploadTimestamp)} month="long" day="numeric" />;
    const time = (
      <Time date={new Date(lastUploadTimestamp)} hour12 hour="numeric" minute="2-digit" />
    );
    const { syncMethod } = this.props;
    let syncMethodText = (
      <Text>
        {lastUploadFiles.length} CSV files received <b>{date}</b> at <b>{time}</b> |{' '}
      </Text>
    );
    if (syncMethod == 'Clever' || syncMethod == 'SFTP') {
      syncMethodText = (
        <Text>
          {lastUploadFiles.length} CSV files received <b>{date}</b> at <b>{time}</b> via{' '}
          <b>{this.props.syncMethod}</b> |{' '}
        </Text>
      );
    }

    return (
      <Row size="xsmall" data-test="last-upload-info-row">
        {syncMethodText}
        <Link weight="semibold" href={filesZipUrl || '#'}>
          Download CSV files
        </Link>
      </Row>
    );
  }

  renderRosteringEnforcementCheckbox(organization: Organization) {
    const { canRosteredUsersLeaveOrg } = organization.permissions;
    return (
      <Row>
        <Container size="medium" align="left" flush>
          <Checkbox
            data-test="rostering-enforcement-checkbox"
            label="Enable rostering enforcement"
            labelNote={
              <Text color="thunder" weight="normal">
                This removes the option for SIS-linked users to opt out of organization-wide
                messages. <br />
                They can still leave individual classes within the organization.{' '}
                <Link href={LinkConstants.ROSTERING_ENFORCEMENT} target="_blank">
                  Learn more
                </Link>
              </Text>
            }
            checked={!canRosteredUsersLeaveOrg}
            onChange={() => this.props.handleRosteringEnforcementChange(!canRosteredUsersLeaveOrg)}
          />
        </Container>
      </Row>
    );
  }

  renderBody(organization: Organization) {
    const { orgId, syncMethod, router } = this.props;
    if (!CurrentUser.isSignedIn()) {
      return null;
    }

    const { canOnlySeeRosteringDashboard } = organization.rosteringPermissions;
    const currentUser = CurrentUser.getUser();

    return (
      <section className="AdminAdminsView">
        {this.renderHeader(organization)}
        {canOnlySeeRosteringDashboard &&
          currentUser.hasFeature('rostering_enforcement_checkbox.web.sept-19') &&
          this.renderRosteringEnforcementCheckbox(organization)}
        <AdminSisSyncSchoolTable
          orgUuid={organization.uuid}
          orgId={orgId}
          rosteringPermissions={organization.rosteringPermissions}
          syncMethod={syncMethod}
          router={router}
          childOrgs={organization.childOrganizations}
        />
      </section>
    );
  }

  render() {
    return (
      <div className="AdminDistrictSisDashboard">{this.renderBody(this.props.organization)}</div>
    );
  }
}
